// Helpers for the UI translation workflow:
// English master list (talmidsdb/uiStrings) -> export sheet -> translator fills target column
// -> import sheet into talmidsdb/translations/{languageId}/{stringId}.
import { read, utils, writeFile } from "xlsx";
import { getAuth } from "firebase/auth";
import { getDatabase, ref, update } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import Firebase from "../Firebase/Firebase";
import { invalidateDemoData } from "../Firebase/databaseHooks";

const db = getDatabase(Firebase);

export const UI_STRING_KEY_PATTERN = /^[A-Za-z0-9_.-]+$/;

const clean = (value) => String(value ?? "").trim();

const toList = (value) => {
	if (Array.isArray(value)) return value.filter(Boolean);
	if (value && typeof value === "object") return Object.values(value).filter(Boolean);
	return [];
};

export const getLanguageName = (language) =>
	clean(language?.languageName || language?.language) || "Translation";

/** "translated" | "stale" (English changed since it was translated) | "missing" */
export function getTranslationStatus(uiString, translation) {
	if (!translation || !clean(translation.text)) return "missing";
	return clean(translation.sourceEnglish) === clean(uiString?.english) ? "translated" : "stale";
}

export function getTranslationProgress(uiStrings, languageTranslations) {
	const progress = { total: 0, translated: 0, stale: 0, missing: 0 };
	toList(uiStrings).forEach((uiString) => {
		progress.total += 1;
		progress[getTranslationStatus(uiString, languageTranslations?.[uiString.id])] += 1;
	});
	return progress;
}

const sortByKey = (list) => [...list].sort((a, b) => clean(a.key).localeCompare(clean(b.key)));

/**
 * mode "missing": only strings that are untranslated or stale.
 * mode "all": every string, pre-filled with current (non-stale) translations.
 */
export function buildExportRows(uiStrings, languageTranslations, languageName, mode = "missing") {
	const rows = [];
	sortByKey(toList(uiStrings)).forEach((uiString) => {
		const translation = languageTranslations?.[uiString.id];
		const status = getTranslationStatus(uiString, translation);
		if (mode === "missing" && status === "translated") return;
		rows.push({
			Key: uiString.key,
			English: uiString.english,
			[languageName]: status === "translated" ? translation.text : "",
		});
	});
	return rows;
}

function writeSheet(rows, headers, fileName) {
	const worksheet = utils.json_to_sheet(rows, { header: headers });
	const workbook = utils.book_new();
	utils.book_append_sheet(workbook, worksheet, "Translations");
	writeFile(workbook, fileName);
}

export function exportTranslationSheet({ uiStrings, languageTranslations, language, mode }) {
	const languageName = getLanguageName(language);
	const rows = buildExportRows(uiStrings, languageTranslations, languageName, mode);
	const suffix = mode === "all" ? "all" : "missing";
	writeSheet(rows, ["Key", "English", languageName], `${languageName}-translations-${suffix}.xlsx`);
	return rows.length;
}

export function exportUiStringsSheet(uiStrings) {
	const rows = sortByKey(toList(uiStrings)).map((uiString) => ({
		Key: uiString.key,
		English: uiString.english,
		Module: uiString.module || "",
	}));
	writeSheet(rows, ["Key", "English", "Module"], "ui-strings-english.xlsx");
	return rows.length;
}

/** Reads the first worksheet of an .xlsx/.xls/.csv file as [{ header: value }]. */
export function readSheetRows(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (evt) => {
			try {
				const workbook = read(evt.target.result, { type: "array" });
				const worksheet = workbook.Sheets[workbook.SheetNames[0]];
				resolve(utils.sheet_to_json(worksheet, { defval: "" }));
			} catch (error) {
				reject(error);
			}
		};
		reader.onerror = () => reject(reader.error);
		reader.readAsArrayBuffer(file);
	});
}

const findHeader = (headers, ...names) => {
	const wanted = names.map((name) => clean(name).toLowerCase());
	return headers.find((header) => wanted.includes(clean(header).toLowerCase()));
};

/**
 * Matches sheet rows to uiStrings and returns the multi-path updates (relative to talmidsdb)
 * plus a summary. Rows match by Key when that column exists, otherwise by English text,
 * so legacy two-column "English | Translation" files still import.
 */
export function buildTranslationUpdates(rows, { uiStrings, language, languageId }) {
	const headers = rows.length ? Object.keys(rows[0]) : [];
	const keyHeader = findHeader(headers, "Key");
	const englishHeader = findHeader(headers, "English");
	const translationHeader =
		findHeader(headers, getLanguageName(language), "Translation") ||
		headers.find((header) => header !== keyHeader && header !== englishHeader && clean(header).toLowerCase() !== "module");

	const summary = { updated: 0, empty: 0, unmatched: [] };
	const updates = {};

	if (!translationHeader || (!keyHeader && !englishHeader)) {
		summary.error = "The file needs a Key or English column and a translation column.";
		return { updates, summary };
	}

	const byKey = new Map();
	const byEnglish = new Map();
	toList(uiStrings).forEach((uiString) => {
		byKey.set(clean(uiString.key), uiString);
		if (!byEnglish.has(clean(uiString.english))) byEnglish.set(clean(uiString.english), uiString);
	});

	const now = Date.now();
	const updatedBy = getAuth(Firebase).currentUser?.uid || null;

	rows.forEach((row) => {
		const key = keyHeader ? clean(row[keyHeader]) : "";
		const english = englishHeader ? clean(row[englishHeader]) : "";
		const text = clean(row[translationHeader]);
		if (!key && !english) return;

		const uiString = (key && byKey.get(key)) || (english && byEnglish.get(english));
		if (!uiString) {
			summary.unmatched.push(key || english);
			return;
		}
		if (!text) {
			summary.empty += 1;
			return;
		}

		updates[`translations/${languageId}/${uiString.id}`] = {
			stringId: uiString.id,
			text,
			sourceEnglish: uiString.english,
			updatedAt: now,
			updatedBy,
		};
		summary.updated += 1;
	});

	return { updates, summary };
}

export async function saveTalmidsUpdates(updates, invalidatePaths) {
	if (!Object.keys(updates).length) return;
	await update(ref(db, "talmidsdb"), updates);
	invalidateDemoData(invalidatePaths);
}

/** Parses a translation file and writes matched translations for one language. */
export async function importTranslationFile(file, { uiStrings, language, languageId, touchLanguage = true }) {
	const rows = await readSheetRows(file);
	const { updates, summary } = buildTranslationUpdates(rows, { uiStrings, language, languageId });
	if (summary.error || summary.updated === 0) return summary;

	if (touchLanguage) {
		updates[`languages/${languageId}/translationsUpdatedAt`] = Date.now();
	}
	await saveTalmidsUpdates(updates, [
		"talmidsdb/translations",
		"talmidsdb/translations/" + languageId,
		"talmidsdb/languages",
		"talmidsdb/languages/" + languageId,
	]);
	return summary;
}

/**
 * Upserts English master strings from a "Key | English | Module" sheet.
 * Changing the English of an existing key bumps updatedAt, which makes its
 * translations stale in every language.
 */
export async function importUiStringsFile(file, uiStrings) {
	const rows = await readSheetRows(file);
	const headers = rows.length ? Object.keys(rows[0]) : [];
	const keyHeader = findHeader(headers, "Key");
	const englishHeader = findHeader(headers, "English");
	const moduleHeader = findHeader(headers, "Module");

	const summary = { created: 0, updated: 0, unchanged: 0, invalid: [] };
	if (!keyHeader || !englishHeader) {
		summary.error = "The file needs Key and English columns.";
		return summary;
	}

	const byKey = new Map(toList(uiStrings).map((uiString) => [clean(uiString.key), uiString]));
	const updates = {};
	const now = Date.now();

	rows.forEach((row) => {
		const key = clean(row[keyHeader]);
		const english = clean(row[englishHeader]);
		const module = moduleHeader ? clean(row[moduleHeader]) : "";
		if (!key && !english) return;
		if (!UI_STRING_KEY_PATTERN.test(key) || !english) {
			summary.invalid.push(key || english);
			return;
		}

		const existing = byKey.get(key);
		if (!existing) {
			const id = uuidv4();
			const record = { id, key, english, module, createdAt: now, updatedAt: now };
			updates[`uiStrings/${id}`] = record;
			byKey.set(key, record);
			summary.created += 1;
			return;
		}

		const englishChanged = clean(existing.english) !== english;
		const moduleChanged = moduleHeader && clean(existing.module) !== module;
		if (!englishChanged && !moduleChanged) {
			summary.unchanged += 1;
			return;
		}
		if (englishChanged) {
			updates[`uiStrings/${existing.id}/english`] = english;
			updates[`uiStrings/${existing.id}/updatedAt`] = now;
		}
		if (moduleChanged) updates[`uiStrings/${existing.id}/module`] = module;
		summary.updated += 1;
	});

	await saveTalmidsUpdates(updates, ["talmidsdb/uiStrings"]);
	return summary;
}

export function describeImportSummary(summary) {
	if (summary.error) return summary.error;
	const parts = [`${summary.updated} translation(s) imported`];
	if (summary.empty) parts.push(`${summary.empty} left empty`);
	if (summary.unmatched.length) {
		const sample = summary.unmatched.slice(0, 5).join(", ");
		parts.push(`${summary.unmatched.length} not found in UI Strings (${sample}${summary.unmatched.length > 5 ? ", …" : ""})`);
	}
	return parts.join(" · ");
}
